/**
 * BYRÅETS EGEN BOOKING — en avtale gjort utenfor Tvitvi (QA runde 12, #35, migrering 0283).
 *
 * =================================================================================================
 * Atle: «Byrået kan opprette booking selv, uten innkommende forespørsel. Bakgrunn: Avtalen er gjort
 * utenfor Tvitvi, eller arrangøren er ikke i Tvitvi.»
 *
 * =================================================================================================
 * DETTE ER IKKE EN FORESPØRSEL, OG DET ER MÅLT.
 *
 * `booking_enquiries.link_id` og `.counterparty_organization_id` er begge NOT NULL — altså en
 * godtatt kobling mellom to hus og en org-id. En arrangør som ikke er i Tvitvi har ingen av
 * delene. Se filhodet i migrering 0283 for hele målingen.
 *
 * En forespørsel er dessuten en MELDING fra noen. Denne raden er et FAKTUM byrået fører, og
 * skillet er hele grunnen til at den ikke skal ligge i innboksen.
 *
 * =================================================================================================
 * TRE SLAGS ARRANGØR, ÉN STRENG Å VISE.
 *
 * `organiserName` er alltid fylt og er det lista og kalenderen tegner. `organiserKind` sier hva
 * navnet peker på, og basen håndhever at pekeren og ordet er enige.
 */
import { z } from 'zod';

import { engagementRefDtoSchema } from './engagements.js';

/** Tvitvi-org, husets eget firmaregister (#9), eller bare et navn. */
export const AGENCY_BOOKING_ORGANISER_KINDS = ['chorus_org', 'external', 'free_text'] as const;
export type AgencyBookingOrganiserKind = (typeof AGENCY_BOOKING_ORGANISER_KINDS)[number];

/**
 * Bekreftet, holdt, tentativ — byråets egen status.
 *
 * IKKE `performanceStatus`. Den beskriver en programpost hos festivalen og har ni verdier om
 * kontrakt og oppgjør; dette er de tre en agent skriver ned etter en telefon.
 */
export const AGENCY_BOOKING_STATUSES = ['confirmed', 'hold', 'tentative'] as const;
export type AgencyBookingStatus = (typeof AGENCY_BOOKING_STATUSES)[number];

const isoDate = z.iso.date();
const shortText = z.string().trim().max(200);

export const agencyBookingDtoSchema = z
  .object({
    id: z.uuid(),
    bookingArtistId: z.uuid(),
    startsOn: isoDate,
    endsOn: isoDate,
    organiserKind: z.enum(AGENCY_BOOKING_ORGANISER_KINDS),
    organiserName: z.string(),
    counterpartyOrganizationId: z.uuid().nullable(),
    contactCompanyId: z.uuid().nullable(),
    venue: z.string().nullable(),
    bookingArtistFormatId: z.uuid().nullable(),
    /** Navnet på formatet, lest gjennom pekeren. Null når raden ikke peker på et format. */
    formatName: z.string().nullable(),
    /** Hvor mange som reiser i det valgte formatet (§5.2). Prefyller, eier ikke. */
    travellingParty: z.number().int().nullable(),
    feeMinor: z.number().int().nullable(),
    currency: z.string().length(3),
    terms: z.string().nullable(),
    status: z.enum(AGENCY_BOOKING_STATUSES),
    /** «Opprettet av byrået» (#35, siste kulepunkt). Én verdi i dag; kolonnen finnes for neste. */
    source: z.literal('agency'),
    /**
     * Den delte advancen, når en slik finnes. ALLTID NULL I DAG — se sak 161: `shared_advances`
     * krever en forespørsel og et motpartshus, og en booking byrået har skrevet inn har ingen av
     * delene. Feltet står i kontrakten så klienten ikke må endres når den dagen kommer.
     */
    sharedAdvanceId: z.uuid().nullable(),
    /**
     * FORESPØRSELEN SOM KNYTTER BOOKINGEN TIL ARRANGØRENS PROGRAMPOST (sak 212).
     *
     * Samme artist, samme motpartshus og en spilledato inne i bookingen — id-er, aldri navn. Det er
     * den «Del reisefølget» deler på. Null for en arrangør utenfor Tvitvi, eller når arrangøren
     * ikke har sendt noe bud gjennom Tvitvi; da er knappen deaktivert med en forklaring.
     */
    linkedEnquiryId: z.uuid().nullable(),
    /** Bookingsaken (0927): statusen alle listene leser (KONTRAKT-B, B-eng). */
    engagement: engagementRefDtoSchema.nullable().optional(),
    createdAt: z.iso.datetime({ offset: true }),
  })
  .meta({ id: 'AgencyBooking' });
export type AgencyBookingDto = z.infer<typeof agencyBookingDtoSchema>;

export const agencyBookingListResponseSchema = z
  .object({ items: z.array(agencyBookingDtoSchema) })
  .meta({ id: 'AgencyBookingListResponse' });
export type AgencyBookingListResponse = z.infer<typeof agencyBookingListResponseSchema>;

const agencyBookingFields = z.object({
  startsOn: isoDate,
  /** Utelatt betyr én kveld: serveren setter den lik `startsOn`. */
  endsOn: isoDate.optional(),
  organiserKind: z.enum(AGENCY_BOOKING_ORGANISER_KINDS),
  /**
   * Navnet. PÅKREVD OGSÅ NÅR EN PEKER FØLGER MED, og det er ikke slurv: raden lagrer en lesekopi
   * (se 0283), og å la serveren slå den opp ville gjort en booking fra i fjor om hver gang et
   * firma bytter navn.
   */
  organiserName: z.string().trim().min(1).max(200),
  counterpartyOrganizationId: z.uuid().nullish(),
  contactCompanyId: z.uuid().nullish(),
  venue: shortText.nullish(),
  bookingArtistFormatId: z.uuid().nullish(),
  feeMinor: z.number().int().min(0).max(100_000_000_00).nullish(),
  currency: z.string().length(3).optional(),
  terms: z.string().trim().max(4000).nullish(),
  status: z.enum(AGENCY_BOOKING_STATUSES).optional(),
});

/**
 * Pekeren må passe til ordet — den samme skranken basen har, sagt i kontrakten så feilen blir en
 * 400 med et felt og ikke en 503 fra en skranke.
 */
function pointerMatchesKind(value: {
  organiserKind: AgencyBookingOrganiserKind;
  counterpartyOrganizationId?: string | null | undefined;
  contactCompanyId?: string | null | undefined;
}): boolean {
  if (value.organiserKind === 'chorus_org') {
    return value.counterpartyOrganizationId != null && value.contactCompanyId == null;
  }
  if (value.organiserKind === 'external') {
    return value.contactCompanyId != null && value.counterpartyOrganizationId == null;
  }
  return value.counterpartyOrganizationId == null && value.contactCompanyId == null;
}

export const createAgencyBookingRequestSchema = agencyBookingFields
  .extend({
    /**
     * «Opprett likevel» (#35: «Duplikater: Match på artist + dato + arrangør, og foreslå
     * sammenslåing hvis begge parter oppretter»).
     *
     * Uten den svarer serveren 409 med raden den fant. To sett samme kveld ER lovlig — en matiné
     * og en kveldsforestilling — så dette er et SPØRSMÅL og ikke en skranke.
     */
    force: z.boolean().optional(),
  })
  .refine((value) => value.endsOn === undefined || value.endsOn >= value.startsOn, {
    message: 'endsOn must not be before startsOn.',
    path: ['endsOn'],
  })
  .refine(pointerMatchesKind, {
    message: 'The organiser pointer must match organiserKind.',
    path: ['organiserKind'],
  })
  .meta({ id: 'CreateAgencyBookingRequest' });
export type CreateAgencyBookingRequest = z.infer<typeof createAgencyBookingRequestSchema>;

export const updateAgencyBookingRequestSchema = agencyBookingFields
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .refine(
    (value) =>
      value.startsOn === undefined || value.endsOn === undefined || value.endsOn >= value.startsOn,
    { message: 'endsOn must not be before startsOn.', path: ['endsOn'] },
  )
  .refine(
    (value) =>
      value.organiserKind === undefined ||
      pointerMatchesKind({
        organiserKind: value.organiserKind,
        counterpartyOrganizationId: value.counterpartyOrganizationId,
        contactCompanyId: value.contactCompanyId,
      }),
    { message: 'The organiser pointer must match organiserKind.', path: ['organiserKind'] },
  )
  .meta({ id: 'UpdateAgencyBookingRequest' });
export type UpdateAgencyBookingRequest = z.infer<typeof updateAgencyBookingRequestSchema>;

/**
 * «FINNES DENNE KVELDEN ALT?», stilt MENS skjemaet fylles ut (#35, duplikater).
 *
 * En egen spørring framfor bare en 409, fordi forslaget om sammenslåing skal komme før man
 * trykker. `endsOn` utelatt betyr én kveld, som i opprettelsen.
 */
export const agencyBookingDuplicateQuerySchema = z
  .object({
    startsOn: isoDate,
    endsOn: isoDate.optional(),
    organiserName: z.string().trim().min(1).max(200),
    counterpartyOrganizationId: z.uuid().optional(),
    contactCompanyId: z.uuid().optional(),
  })
  .meta({ id: 'AgencyBookingDuplicateQuery' });
export type AgencyBookingDuplicateQuery = z.infer<typeof agencyBookingDuplicateQuerySchema>;

/**
 * SVARET. Det bærer HELE raden og ikke bare en id, fordi spørsmålet skjermen stiller er «er dette
 * den samme kvelden?» — og det kan ingen svare på uten å se arrangøren, stedet og honoraret.
 */
export const agencyBookingDuplicateSchema = z
  .object({ duplicate: agencyBookingDtoSchema.nullable() })
  .meta({ id: 'AgencyBookingDuplicate' });
export type AgencyBookingDuplicate = z.infer<typeof agencyBookingDuplicateSchema>;
